import { useEffect, useMemo } from "react";

/**
 * @typedef {Object} MutationObserverOptions
 *
 * @property {boolean} [childList]
 *   Set to `true` to observe the addition or removal of child nodes
 *   (only immediate children unless `subtree` is also true).
 *
 * @property {boolean} [attributes]
 *   Set to `true` to observe changes to attributes on the target node.
 *
 * @property {boolean} [characterData]
 *   Set to `true` to observe changes to the text content (character data)
 *   of the target node.
 *
 * @property {boolean} [subtree]
 *   Set to `true` to extend observations to all descendants of the target,
 *   not just the target node itself.
 *
 * @property {string[]} [attributeFilter]
 *   An array of attribute names to be monitored. If specified,
 *   only changes to these attributes will trigger a mutation record.
 *
 * @property {boolean} [attributeOldValue]
 *   When true and `attributes` is also true, records the previous
 *   value of the changed attribute in `MutationRecord.oldValue`.
 *
 * @property {boolean} [characterDataOldValue]
 *   When true and `characterData` is also true, records the previous
 *   text value in `MutationRecord.oldValue`.
 */

/**
 * A custom hook that will observe a target element on the DOM for mutations.
 * @param {object} param0
 * @param {string} param0.targetSelector - A css selector for the element to observe. Examples: "body", "#myId", ".myClass"
 * @param {function} param0.handleMutation - A callback function that will be called when a mutation is observed.
 * @param {MutationObserverOptions} param0.options
 * @returns null
 * 
 * @example
 * This WAS used in useManageProfileField.js. But this could come in handy again.
 * It watched for when the custom field manager was removed from the DOM and closed the modal
 * 
 * useMutationObserver2({
    targetSelector: "body",
    handleMutation: closeModalWhenFieldManagerClosed,
    options: { childList: true },
  });
 */
export default function useMutationObserver2({
  targetSelector,
  handleMutation,
  options,
}) {
  const observer = useMemo(
    () =>
      new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          handleMutation(mutation);
        });
      }),
    [handleMutation]
  );

  useEffect(() => {
    if (!targetSelector) return;
    let el = document.querySelector(targetSelector);
    if (observer && el) {
      observer.observe(el, options);
      return () => observer.disconnect();
    }
  }, [targetSelector, observer, options]);
}
